import React, { useState, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { QRCodeCanvas } from "qrcode.react";
import { Share2, Link2, Check, Code2, Copy, Bitcoin, RefreshCw, QrCode, Download, Megaphone } from "lucide-react";
import { toast } from "sonner";

// simple inline brand icons (no extra deps)
const XIcon = (p) => (<svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M18.9 2H22l-7.3 8.3L23.3 22h-6.8l-5.3-6.9L5.1 22H2l7.8-8.9L1 2h6.9l4.8 6.3L18.9 2Zm-1.2 18h1.9L7.4 4H5.4l12.3 16Z" /></svg>);
const FbIcon = (p) => (<svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.2c-1.2 0-1.6.8-1.6 1.5V12h2.7l-.4 2.9h-2.3v7A10 10 0 0 0 22 12Z" /></svg>);
const WaIcon = (p) => (<svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M17.5 14.4c-.3-.1-1.7-.8-2-.9-.3-.1-.5-.1-.6.1-.2.3-.7.9-.9 1-.2.2-.3.2-.6.1-.3-.1-1.2-.5-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6l.4-.5c.1-.2.2-.3.3-.5 0-.2 0-.4 0-.5 0-.1-.6-1.5-.9-2.1-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.2.3-.9.9-.9 2.2s1 2.5 1.1 2.7c.1.2 1.9 3 4.7 4.2.7.3 1.2.5 1.6.6.7.2 1.3.2 1.8.1.5-.1 1.7-.7 1.9-1.4.2-.7.2-1.2.2-1.4-.1-.1-.3-.2-.5-.3ZM12 2a10 10 0 0 0-8.6 15l-1.3 4.7 4.8-1.3A10 10 0 1 0 12 2Z" /></svg>);

export default function ShareKit({ campaignId, slug, title, crypto = true }) {
  // Share link routes through the backend so social crawlers get rich OG previews;
  // humans are instantly redirected to the campaign page.
  const url = `${window.location.origin}/api/s/${slug}`;
  const [copied, setCopied] = useState(false);
  const [showEmbed, setShowEmbed] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);
  const [showKit, setShowKit] = useState(false);
  const qrRef = useRef(null);
  const text = `Support "${title}" on FightFund`;

  const captions = [
    `🙏 We're rallying support for "${title}". Every dollar helps us fight the good fight — please donate or share: ${url}`,
    `Friends, this cause means a lot to us. If you can chip in or just spread the word, it would mean the world 💙 ${url}`,
    `Small gifts add up fast. Help us reach our goal for "${title}" today: ${url}`,
  ];

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `fightfund-${slug}-qr.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
    track();
  };

  const copyCaption = async (cap) => {
    try { await navigator.clipboard.writeText(cap); track(); toast.success("Caption copied!"); } catch { toast.error("Could not copy"); }
  };

  const track = () => { if (campaignId) api.post(`/campaigns/${campaignId}/share`).catch(() => {}); };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true); track();
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Could not copy link"); }
  };

  const embedCode = `<iframe src="${window.location.origin}/embed/${slug}" width="360" height="440" style="border:1px solid #E5E7EB;border-radius:12px;" title="Donate to ${title}"></iframe>`;
  const copyEmbed = async () => {
    try { await navigator.clipboard.writeText(embedCode); setEmbedCopied(true); track(); setTimeout(() => setEmbedCopied(false), 2000); } catch {}
  };

  const shareLinks = [
    { icon: XIcon, label: "X", href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, cls: "bg-black" },
    { icon: FbIcon, label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, cls: "bg-[#1877F2]" },
    { icon: WaIcon, label: "WhatsApp", href: `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`, cls: "bg-[#25D366]" },
  ];

  return (
    <div className="rounded-2xl border border-ff-border bg-white p-5 shadow-sm" data-testid="share-kit">
      <div className="flex items-center gap-2 font-heading text-sm font-bold text-ff-blue">
        <Share2 className="h-4 w-4 text-ff-action" /> Share this fundraiser
      </div>
      <p className="mt-1 text-xs text-ff-medium">Sharing is the #1 way to help a campaign succeed.</p>
      {crypto && (
        <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#ECFDF5] px-2.5 py-1 text-[11px] font-bold text-ff-green" data-testid="share-crypto-badge">
          <Bitcoin className="h-3 w-3" /> Now accepting crypto
        </span>
      )}

      <button onClick={copy} data-testid="share-copy-link"
        className="mt-3 flex w-full items-center justify-between gap-2 rounded-lg border border-ff-border px-3 py-2.5 text-sm font-semibold text-ff-blue transition-colors duration-200 hover:border-ff-action">
        <span className="flex items-center gap-2 truncate"><Link2 className="h-4 w-4 shrink-0" /> <span className="truncate">{url.replace(/^https?:\/\//, "")}</span></span>
        {copied ? <Check className="h-4 w-4 text-ff-green" /> : <Copy className="h-4 w-4 text-ff-medium" />}
      </button>

      <div className="mt-3 grid grid-cols-3 gap-2">
        {shareLinks.map((s) => (
          <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" onClick={track}
            data-testid={`share-${s.label.toLowerCase()}`}
            className={`flex items-center justify-center gap-1.5 rounded-lg py-2.5 text-xs font-bold text-white transition-transform duration-200 hover:scale-[1.03] ${s.cls}`}>
            <s.icon className="h-4 w-4" /> {s.label}
          </a>
        ))}
      </div>

      <div className="mt-3 flex items-center justify-between gap-2">
        <button onClick={() => setShowEmbed((v) => !v)} data-testid="share-embed-toggle"
          className="flex items-center gap-1.5 text-xs font-semibold text-ff-medium hover:text-ff-action">
          <Code2 className="h-3.5 w-3.5" /> {showEmbed ? "Hide" : "Get"} embed code
        </button>
        <a href={`https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(url)}`}
          target="_blank" rel="noopener noreferrer" data-testid="share-refresh-preview"
          title="Force social platforms to re-fetch the latest preview after updates"
          className="flex items-center gap-1 text-[11px] font-semibold text-ff-medium hover:text-ff-action">
          <RefreshCw className="h-3 w-3" /> Refresh preview
        </a>
      </div>
      {showEmbed && (
        <div className="mt-2" data-testid="share-embed-box">
          <textarea readOnly value={embedCode} rows={3}
            className="w-full rounded-lg border border-ff-border bg-[#F3F4F6] p-2 font-mono text-[10px] text-ff-dark" />
          <Button size="sm" onClick={copyEmbed} data-testid="share-embed-copy" className="mt-2 w-full bg-ff-blue text-white hover:bg-ff-blue/90">
            {embedCopied ? <><Check className="mr-1.5 h-3.5 w-3.5" /> Copied</> : "Copy embed code"}
          </Button>
        </div>
      )}

      <div className="mt-3 border-t border-ff-border pt-3">
        <button onClick={() => setShowKit((v) => !v)} data-testid="share-kit-toggle"
          className="flex items-center gap-1.5 text-xs font-semibold text-ff-medium hover:text-ff-action">
          <QrCode className="h-3.5 w-3.5" /> {showKit ? "Hide" : "QR code & ready-made captions"}
        </button>
        {showKit && (
          <div className="mt-3 space-y-4" data-testid="share-kit-box">
            <div className="flex flex-col items-center rounded-xl border border-ff-border bg-white p-4">
              <div ref={qrRef}><QRCodeCanvas value={url} size={148} level="M" includeMargin fgColor="#143257" /></div>
              <p className="mt-2 text-center text-[11px] text-ff-medium">Print on flyers, church bulletins &amp; event signs.</p>
              <Button size="sm" onClick={downloadQR} data-testid="share-qr-download" className="mt-2 bg-ff-blue text-white hover:bg-ff-blue/90">
                <Download className="mr-1.5 h-3.5 w-3.5" /> Download QR
              </Button>
            </div>
            <div>
              <div className="flex items-center gap-1.5 text-xs font-bold text-ff-blue"><Megaphone className="h-3.5 w-3.5 text-ff-action" /> Ready-to-post captions</div>
              <div className="mt-2 space-y-2">
                {captions.map((cap, i) => (
                  <button key={i} onClick={() => copyCaption(cap)} data-testid={`share-caption-${i}`}
                    className="group flex w-full items-start gap-2 rounded-lg border border-ff-border bg-[#F9FAFB] p-2.5 text-left text-xs text-ff-dark transition-colors duration-200 hover:border-ff-action">
                    <span className="flex-1">{cap}</span>
                    <Copy className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ff-medium group-hover:text-ff-action" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
